/**
 * useMyData — consumer hook for MyDataPort (033 US1, T024).
 *
 * Reads the `myData` port from PortsContext, calls `getMyData()`, and
 * resolves async results via React.use(). Throws if the provider or the port
 * slot is absent.
 *
 * Promise-caching discipline — WHY THIS DIFFERS from useMemberProfile's
 * useRef pattern: this port is genuinely ASYNC (the real tRPC reader; the
 * sibling member ports are synchronous demo adapters, so their ref cache is
 * never exercised by a first-mount suspend). When a component suspends during
 * its INITIAL mount, React discards the in-progress fiber — refs from the
 * aborted attempt do not survive the retry, so a useRef-cached Promise would
 * be re-created every retry and the boundary would loop forever. The cache
 * therefore lives OUTSIDE React: a module-level WeakMap keyed by the port
 * instance. It survives the suspend-retry, resets when the composition swaps
 * the adapter (new key), and is garbage-collected with the port. One read per
 * port instance per app lifetime — the consolidated GDPR view re-fetches on
 * reload, which matches its access-surface semantics.
 *
 * Only call inside components wrapped in a Suspense boundary: the Profile
 * route wraps MyDataSection in PortErrorBoundary(myData) + Suspense.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import React, { useContext } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type { MyDataPort, MyDataView } from './MyDataPort.js';

/**
 * Cache entry: the in-flight/settled promise plus a rejection marker. The
 * marker (not bare eviction) is what makes failure recovery correct — see the
 * rejection-handling note in {@link useMyData}.
 */
interface ViewCacheEntry {
  readonly promise: PromiseLike<MyDataView>;
  rejected: boolean;
}

const viewPromiseCache = new WeakMap<MyDataPort, ViewCacheEntry>();

function isPromiseLike(
  value: MyDataView | PromiseLike<MyDataView>,
): value is PromiseLike<MyDataView> {
  // A thenable requires a CALLABLE `then` — `'then' in value` alone would
  // misclassify a plain data object carrying a non-function `then` field and
  // send a non-thenable into React.use.
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

export function useMyData(): MyDataView {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useMyData: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.myData === undefined) {
    throw new Error(
      'useMyData: myData port is not registered. This feature requires the My-data port to be configured.',
    );
  }

  const port = ports.myData;

  let entry = viewPromiseCache.get(port);
  if (entry === undefined) {
    const viewOrPromise = port.getMyData();
    if (!isPromiseLike(viewOrPromise)) {
      // Synchronous adapter (test fixture) — nothing to suspend on.
      return viewOrPromise;
    }
    const newEntry: ViewCacheEntry = { promise: viewOrPromise, rejected: false };
    viewOrPromise.then(undefined, () => {
      newEntry.rejected = true;
    });
    viewPromiseCache.set(port, newEntry);
    entry = newEntry;
  } else if (entry.rejected) {
    // Rejection handling (review Fix 1): the port is an app-lifetime singleton,
    // so a permanently cached REJECTED promise would re-throw the same stale
    // failure on every later mount — one transient blip would brick the view
    // until a full page reload, falsifying the fallback's "try again later".
    // But eager eviction (in the promise's own catch, or synchronously here)
    // is ALSO wrong: after the rejection React re-renders, and when THAT
    // render throws it synchronously retries the render once more before
    // engaging the boundary — an already-evicted cache would make the retry
    // fire a fresh fetch, so the boundary never shows and a persistently
    // failing port re-fetches in an unbounded loop. Deferring the eviction to
    // a microtask lands it AFTER React's synchronous error pass has committed
    // the boundary fallback (all retry attempts still observe the rejected
    // entry and rethrow), while any LATER mount — the navigate-away-and-back
    // recovery — finds an empty cache and re-fetches. One fetch per mount.
    queueMicrotask(() => viewPromiseCache.delete(port));
  }

  // React.use may be called conditionally (unlike other hooks); the stable
  // Promise identity across the suspend-retry is what lets the boundary
  // resolve instead of looping.
  return React.use(entry.promise);
}

/**
 * Returns the configured reader for an interaction that fetches a further
 * My Data page. The caller owns the short-lived request and its cancellation;
 * this hook only validates the shared dependency.
 */
export function useMyDataAnalyticsReader(): MyDataPort {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useMyDataAnalyticsReader: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.myData === undefined) {
    throw new Error(
      'useMyDataAnalyticsReader: myData port is not registered. This feature requires the My-data port to be configured.',
    );
  }

  return ports.myData;
}
